import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Chip, Typography, useTheme, useMediaQuery, Alert, CircularProgress, Avatar } from '@mui/material';
import { SearchAndFilterBar, SkeletonLoader } from '../../components/Common';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ParentForm from '../../components/Forms/ParentForm';
import { parentService, studentService } from '../../services/apiService';
import { formatDate } from '../../utils/dateFormat';
import { showToast } from '../../utils/toast';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';

const Parents = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);
  const drawerWidth = 280;

  // State for parents data
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingParent, setEditingParent] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Search and filter state - initialize from URL or default to active filter (1 = active, 0 = inactive)
  const [filteredParents, setFilteredParents] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const statusParam = searchParams.get('status');
    return {
      status: statusParam || '' // No default filter
    };
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Server-side infinite scroll state
  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const offsetRef = useRef(0);
  const [isAppending, setIsAppending] = useState(false);
  const filtersRef = useRef(filters);

  // Fetch parents from API
  const fetchParents = useCallback(async (search = '', showSearchLoader = false, append = false, customOffset = null, currentFilters = null) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const sentinelEl = sentinelRef.current;
    let io = null;
    if (sentinelEl && sentinelEl._io) {
      io = sentinelEl._io; try { io.unobserve(sentinelEl); } catch (e) { }
    }
    try {
      if (showSearchLoader) {
        setSearchLoading(true);
      } else if (!append) { setLoading(true); } else { setIsAppending(true); }
      setError(null);

      // Build query parameters
      const params = {};
      if (search.trim()) { params.search = search.trim(); }
      params.role_name = 'Parent';
      // Add is_active filter only if a filter is selected
      // Use currentFilters if provided, otherwise use filters from ref
      const activeFilters = currentFilters !== null ? currentFilters : filtersRef.current;
      if (activeFilters.status !== undefined && activeFilters.status !== null && activeFilters.status !== '') {
        const statusValue = String(activeFilters.status);
        params.is_active = statusValue === '1' ? '1' : '0';
      }
      // Pagination
      const nextOffset = customOffset !== null ? customOffset : (append ? offsetRef.current : 0);
      params.limit = pageSize;
      params.offset = nextOffset;

      const response = await parentService.getAll(params);

      if (response.success) {
        const fetchedParents = response.data || [];
        setParents(prev => {
          const merged = append ? [...prev, ...fetchedParents] : fetchedParents;
          // Apply local filters (kept for future use, currently pass-through)
          setFilteredParents(merged);
          return merged;
        });
        setHasMore((fetchedParents.length || 0) === pageSize);
        setOffset(nextOffset + (fetchedParents.length || 0));
        offsetRef.current = nextOffset + (fetchedParents.length || 0);
      } else {
        setError(response.error || 'Failed to fetch parents');
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching parents:', err);
      setError(err.message || 'Failed to fetch parents');
      setHasMore(false);
    } finally {
      // Use setTimeout to batch state updates and prevent focus loss
      setTimeout(() => {
        if (showSearchLoader) {
          setSearchLoading(false);
        }
        setLoading(false);
        setIsAppending(false);
        fetchingRef.current = false;
        const el = sentinelRef.current; if (el && el._io) { try { el._io.observe(el); } catch (e) { } }
      }, 0);
    }
  }, []);

  // Update URL when search or filters change (skip on initial load to avoid conflicts)
  useEffect(() => {
    if (isInitialLoad) return;
    const params = new URLSearchParams();
    if (searchTerm) {
      params.set('search', searchTerm);
    }
    // Only include status filter if one is selected
    if (filters.status) {
      params.set('status', filters.status);
    }
    
    // Only update URL if params have changed to avoid infinite loops
    const currentSearch = searchParams.get('search') || '';
    const currentStatus = searchParams.get('status') || '';
    if (searchTerm !== currentSearch || (filters.status || '') !== currentStatus) {
      setSearchParams(params, { replace: true });
    }
  }, [searchTerm, filters.status, searchParams, setSearchParams, isInitialLoad]);

  // Initialize and load data on component mount
  useEffect(() => {
    if (isInitialLoad) {
      // Load parents without any filter by default
      fetchParents(searchTerm, false, false, 0, filters);
      setIsInitialLoad(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (isInitialLoad) return;
    const timer = setTimeout(() => {
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      fetchParents(searchTerm, true, false, 0, filters);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, isInitialLoad]);

  // Update filters ref when filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Reload when filters change (backend handles filtering)
  useEffect(() => {
    if (!isInitialLoad) {
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      fetchParents(searchTerm, false, false, 0, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  // Apply local filters when filters change (for status filter, backend handles it)
  useEffect(() => {
    // Backend handles status filtering, so we just pass through the parents list
    setFilteredParents(parents);
  }, [parents]);

  // IntersectionObserver for appending
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (!first?.isIntersecting) return;
      if (hasMore && offsetRef.current > 0 && !fetchingRef.current && !loading && !searchLoading) {
        fetchParents(searchTerm, false, true, null, filters);
      }
    }, { root: null, rootMargin: '300px', threshold: 0 });
    el._io = io;
    io.observe(el);
    return () => { try { io.disconnect(); } catch (e) { } if (el && el._io === io) el._io = null; };
  }, [hasMore, loading, searchLoading, searchTerm, filters.status]);

  // Search and filter handlers - memoized to prevent SearchAndFilterBar re-renders
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleFilter = useCallback((filterValues) => {
    setFilters(filterValues);
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    setFilters({ status: '' }); // Reset to no filter
  }, []);

  const handleAdd = useCallback(() => {
    setEditingParent(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((parent) => {
    setEditingParent(parent);
    setModalOpen(true);
  }, []);


  const handleView = useCallback((parent) => {
    navigate(`/users/parents/${parent.id}`);
  }, [navigate]);

  const handleToggleActive = useCallback(async (parent) => {
    const isCurrentlyActive = parent.is_active === '1' || parent.is_active === 1 || parent.is_active === true;
    const newActiveStatus = isCurrentlyActive ? 0 : 1;

    // If trying to deactivate, check for active children
    if (newActiveStatus === 0) {
      try {
        const childrenResponse = await studentService.getAll({ user_id: parent.id });
        const children = childrenResponse.success ? (childrenResponse.data || []) : (Array.isArray(childrenResponse) ? childrenResponse : []);
        
        const hasActiveChildren = children.some(child => 
          child.is_active === '1' || child.is_active === 1 || child.is_active === true
        );

        if (hasActiveChildren) {
          showToast.error('Cannot deactivate parent. This parent has active children. Please deactivate the children first.');
          return;
        }
      } catch (err) {
        console.error('Error checking children:', err);
        showToast.error('Failed to check children status. Please try again.');
        return;
      }
    }

    // Proceed with activation/deactivation
    try {
      const response = await parentService.update(parent.id, { is_active: newActiveStatus });
      
      if (response && !response.success) {
        showToast.error(response.error || 'Failed to update parent status');
        return;
      }

      showToast.success(newActiveStatus === 1 ? 'Parent activated successfully' : 'Parent deactivated successfully');
      
      // Reset pagination and refetch from the beginning with current filters
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      await fetchParents(searchTerm, false, false, 0, filters);
    } catch (err) {
      console.error('Error updating parent status:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update parent status';
      showToast.error(errorMsg);
    }
  }, [fetchParents, searchTerm, filters]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingParent(null);
  }, []);

  const handleModalSave = useCallback(async (formData) => {
    try {
      let response;
      if (editingParent) {
        response = await parentService.update(editingParent.id, formData);
      } else {
        response = await parentService.create(formData);
      }

      if (response && !response.success) {
        const errorMsg = response.error || 'Failed to save parent';
        showToast.error(errorMsg);
        // Don't set error state for save errors - keep table visible, error shown via toast

        // Set field-level errors for duplicate email/phone
        const fieldErrors = {};
        if (errorMsg.toLowerCase().includes('email')) {
          fieldErrors.email = errorMsg;
        }
        if (errorMsg.toLowerCase().includes('phone')) {
          fieldErrors.phone = errorMsg;
        }
        setFormErrors(fieldErrors);
        return;
      }

      showToast.success(editingParent ? 'Parent updated successfully' : 'Parent created successfully');
      setFormErrors({});
      setModalOpen(false);
      // Reset editingParent to null to clear the form after successful creation
      // This ensures the form is reset when the modal opens again
      setEditingParent(null);
      // Reset pagination and refetch from the beginning with current filters
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      await fetchParents(searchTerm, false, false, 0, filters); // Refresh the list with current search and filters
    } catch (err) {
      console.error('Error saving parent:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save parent';
      showToast.error(errorMsg);
      // Don't set error state for save errors - keep table visible, error shown via toast

      // Set field-level errors for duplicate email/phone
      const fieldErrors = {};
      if (errorMsg.toLowerCase().includes('email')) {
        fieldErrors.email = errorMsg;
      }
      if (errorMsg.toLowerCase().includes('phone')) {
        fieldErrors.phone = errorMsg;
      }
      setFormErrors(fieldErrors);
    }
  }, [editingParent, searchTerm, filters]);

  // Filter options for SearchAndFilterBar component - memoized to prevent SearchAndFilterBar re-renders
  const filterOptions = useMemo(() => [
    {
      field: 'status',
      label: 'Status',
      options: [
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' }
      ]
    }
  ], []);


  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          // On mobile: always full width, no margin (sidebar overlays)
          // On desktop: adjust width and margin based on sidebar state
          ml: { xs: 0, md: `${0}px` },
          backgroundColor: 'background.default',
        }}
      >
        <Header onMenuClick={handleDrawerToggle} />
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                  letterSpacing: '0.01em',
                }}
              >
                Parents Management
              </Typography>
              {filters.status && (
                <Chip
                  label={`Filter: ${filters.status === '1' || filters.status === 1 ? 'Active' : 'Inactive'}`}
                  color={filters.status === '1' || filters.status === 1 ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  color="primary"
                  size="small"
                  variant="outlined"
                  onDelete={() => setSearchTerm('')}
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Stack>
          </Stack>
          <SearchAndFilterBar
            title=""
            onSearch={handleSearch}
            onFilter={handleFilter}
            onClear={handleClear}
            onCreate={handleAdd}
            searchPlaceholder="Search by name..."
            filterOptions={filterOptions}
            createButtonText="Add Parent"
            searchValue={searchTerm}
            filterValue={filters}
          />

          <Box sx={{ mt: 2 }}>
            {loading && !searchLoading ? (
              <SkeletonLoader variant="list" count={5} />
            ) : error && !searchLoading ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <>
                {filteredParents.length === 0 ? (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 4, 
                      borderRadius: 3, 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 'calc(100vh - 300px)',
                      width: '100%'
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      No parents found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search or filters to view the list
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: { xs: 1.5, sm: 2 } }}>
                    {filteredParents.map((row) => {
                  const isActive = row.is_active === '1' || row.is_active === 1 || row.is_active === true;
                  return (
                    <Paper key={row.id} variant="outlined" sx={{ p: { xs: 2, sm: 2.4 }, borderRadius: 2, borderColor: 'divider', position: 'relative' }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Avatar 
                            src={row.profile_image || null}
                            alt={row.full_name} 
                            sx={{ width: 48, height: 48, bgcolor: getAvatarColor(row.full_name), color: '#ffffff', fontWeight: 700 }}
                          >
                            {!row.profile_image && getInitials(row.full_name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" fontWeight={700}>{row.full_name}</Typography>
                              <Chip size="small" label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'success' : 'error'} variant="outlined" />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Email:</strong> {row.email || '—'} &nbsp;·&nbsp;
                              <strong>Phone:</strong> {row.phone || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Joined:</strong> {formatDate(row.date_of_joining)}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '100px' }, flexWrap: 'wrap', rowGap: 1 }}>
                          <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleView(row)}>View</Button>
                          <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(row)}>Edit</Button>
                          {isActive ? (
                            <Button 
                              size="small" 
                              color="error" 
                              variant="outlined"
                              onClick={() => handleToggleActive(row)}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button 
                              size="small" 
                              color="success" 
                              variant="outlined"
                              onClick={() => handleToggleActive(row)}
                            >
                              Activate
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                    })}
                    <Box ref={sentinelRef} sx={{ height: 8 }} />
                    {isAppending && (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Parent Form Modal */}
          <ParentForm
            open={modalOpen}
            onClose={() => {
              handleModalClose();
              setFormErrors({});
            }}
            onSubmit={handleModalSave}
            initialData={editingParent}
            title={editingParent ? "Edit Parent" : "Add Parent"}
            externalErrors={formErrors}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Parents;
